<template>
  <div
    class="flex items-center w-[20px] h-[20px] cursor-pointer"
    :class="colorClass"
    @click="addRemoveBookmark(isBookmarked)"
  >
    <!-- CSS mask instead of ion-icon: ion-icon loads its SVG via fetch, which the
         CapacitorHttp patch breaks on iOS native (icon rendered blank). Masks load
         through normal CSS resource loading — same pattern as BottomNavButton. -->
    <span
      class="bookmark-icon-mask w-full h-full"
      :class="{ 'text-[#99A4B2]': !isBookmarked }"
      :style="{
        '--bookmark-icon': `url(${
          isBookmarked ? '/assets/icons/bookmark-filled.svg' : '/assets/icons/bookmark-outline.svg'
        })`,
      }"
      role="img"
      aria-label="Bookmark"
    ></span>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import axios from 'axios';
import { isGICPage } from '@/utils/conference-type';
import { trackEvent } from '@/utils/mixpanel';

export default {
  props: {
    bookmarkId: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapState('user', ['token']),
    ...mapState('filter', ['bookMarkMap']),
    isBookmarked() {
      return this.bookMarkMap?.[this.bookmarkId] || false;
    },
    conferenceType() {
      // Detect if we're on a GIC page
      return isGICPage() ? 'GIC' : 'Global';
    },
    colorClass() {
      return this.conferenceType === 'GIC' ? 'text-orange' : 'text-lightblue';
    },
  },
  methods: {
    applyLocalBookmarkState(bookmarked) {
      // Explicit value (not a toggle): deterministic, and safe to replay for revert
      this.$store.commit('filter/setBookMarkMapItem', {
        slug: this.bookmarkId,
        existSlug: bookmarked,
      });
      this.$store.dispatch('filter/toggleBookMarkList', {
        type: this.type,
        slug: this.bookmarkId,
        flag: bookmarked,
      });
    },
    async addRemoveBookmark(flag) {
      const next = !flag;

      // Optimistic: update the icon and My List immediately — the POST runs in
      // the background so the UI never waits on the network. No refetch needed:
      // the local state is the source of truth for this session, and refetching
      // here races with rapid toggles (an earlier response can wipe a later add).
      this.applyLocalBookmarkState(next);

      try {
        await axios.post(
          process.env.VUE_APP_API_URL + '/videoprogress',
          {
            videoId: this.bookmarkId,
            isBookmarked: next,
          },
          {
            headers: {
              Authorization: `Bearer ${this.token}`,
            },
            params: {
              locale: 'en',
              conferenceType: this.conferenceType,
            },
          },
        );

        // Track only after the server confirms — a failed toggle is reverted
        // below and shouldn't count. flag is the pre-toggle state: !flag means
        // a bookmark was just added, flag means it was just removed.
        try {
          trackEvent('Resource Bookmarked', {
            'bookmarked resource': this.title || this.bookmarkId,
            bookmark_id: this.bookmarkId,
            action: flag ? 'unbookmarked' : 'bookmarked',
            conference_type: this.conferenceType,
            page: this.$route.path,
          });
        } catch {
          // noop
        }
      } catch (e) {
        // Server didn't save it — roll the optimistic update back
        this.applyLocalBookmarkState(flag);
        try {
          this.$store.dispatch('toast/error', "Couldn't save bookmark. Please try again.", {
            root: true,
          });
        } catch {
          // noop
        }
      }
    },
  },
};
</script>

<style scoped>
.bookmark-icon-mask {
  display: block;
  background-color: currentColor;
  -webkit-mask-image: var(--bookmark-icon);
  mask-image: var(--bookmark-icon);
  -webkit-mask-position: center;
  mask-position: center;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-size: contain;
  mask-size: contain;
}
</style>
